import { Box, Card, Stack, Heading, Text, Divider } from 'braid-design-system';
import React from 'react';

import type { ProductBundle } from 'src/types/productBundle';
import {
  BOOST_BUNDLE_DISCOUNT_FEATURE_TOGGLE_ENABLED,
  getBoostBundlePrices,
} from 'src/utils/feature-flags';
import { decimalFormatPriceBand } from 'src/utils/investmentBands';

import { ListingProductNotFoundError } from '../../../ListingProductNotFoundError';

interface BoostBundleProductDetailsProps {
  isListingLus: boolean;
  productBundle: ProductBundle;
}

const BoostBundleProductDetails = ({
  isListingLus,
  productBundle,
}: BoostBundleProductDetailsProps) => {
  // It'd be really nice if we could model this in our types so to eliminate this
  // runtime possibility
  if (
    productBundle.availableEmailBoost ||
    productBundle.availableSearchBoost ||
    !productBundle.boostComponents?.search ||
    !productBundle.boostComponents?.email
  ) {
    return <ListingProductNotFoundError />;
  }

  return (
    <Card data={{ testid: 'boost-bundle-product-details' }}>
      <Box paddingBottom="large">
        <Heading level="3">Boost packages</Heading>
      </Box>

      <Stack space="medium">
        <Box
          display="flex"
          justifyContent="spaceBetween"
          alignItems="center"
          data={{ testid: 'boost-bundle-product-details-search-boost-row' }}
        >
          <Stack space="small">
            <Text weight="strong">Website Search Boost</Text>
            <Text tone="secondary">
              Stand out where it matters – at the top of search results and on
              our homepage for buyers in your location.
            </Text>
          </Stack>

          <Box
            flexShrink={0}
            paddingLeft={{ mobile: 'xsmall', tablet: 'large' }}
          >
            <Text
              weight="regular"
              data={{
                testid: 'boost-bundle-product-details-search-boost-price',
              }}
            >
              {decimalFormatPriceBand(
                productBundle.boostComponents.search.cost / 100,
              )}
            </Text>
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="spaceBetween"
          alignItems="center"
          data={{ testid: 'boost-bundle-product-details-search-email-row' }}
        >
          <Stack space="small">
            <Text weight="strong">Email Boost</Text>
            <Text tone="secondary">
              Reach motivated buyers directly – get featured at the top of our
              daily recommendation emails.
            </Text>
          </Stack>

          <Box
            flexShrink={0}
            paddingLeft={{ mobile: 'xsmall', tablet: 'large' }}
          >
            <Text
              weight="regular"
              data={{
                testid: 'boost-bundle-product-details-email-boost-price',
              }}
            >
              {decimalFormatPriceBand(
                productBundle.boostComponents.email.cost / 100,
              )}
            </Text>
          </Box>
        </Box>

        <Divider />

        <Box
          display="flex"
          justifyContent="spaceBetween"
          alignItems="center"
          paddingY="xxsmall"
        >
          <Text size="large" weight="medium">
            Total:{' '}
          </Text>

          {BOOST_BUNDLE_DISCOUNT_FEATURE_TOGGLE_ENABLED ? (
            <Stack space="small">
              <Text size="large" weight="medium">
                <Box
                  component="span"
                  style={{ textDecoration: 'line-through' }}
                >
                  {decimalFormatPriceBand(
                    getBoostBundlePrices({ isLus: isListingLus }).original /
                      100,
                  )}
                </Box>
              </Text>

              <Text
                size="large"
                weight="medium"
                data={{ testid: 'boost-bundle-product-details-total-price' }}
              >
                {decimalFormatPriceBand(
                  getBoostBundlePrices({ isLus: isListingLus }).discount / 100,
                )}{' '}
                + GST
              </Text>
            </Stack>
          ) : (
            <Text
              size="large"
              weight="medium"
              data={{ testid: 'boost-bundle-product-details-total-price' }}
            >
              {decimalFormatPriceBand(productBundle.info.cost / 100)} + GST
            </Text>
          )}
        </Box>
      </Stack>
    </Card>
  );
};

export default BoostBundleProductDetails;
